import type { Dayjs } from "dayjs";
import { dayjsShow } from "@/app/_utils/dayjs";

/** Default offset when doors_open is unset or matches show start (admin convention). */
export const SHOW_DOORS_OPEN_MINUTES_BEFORE_START = 30;

/** Wall-clock label for America/New_York show times (stored as Eastern). */
export const SHOW_TIMEZONE_LABEL = " EST";

/** WebKit throws RangeError when dayjs formats some invalid tz values. */
function safeFormat(d: Dayjs | null | undefined, fmt: string): string {
  if (!d?.isValid()) return "";
  try {
    return d.format(fmt);
  } catch {
    return "";
  }
}

export function parseShowDateTime(value: string | null | undefined): Dayjs | null {
  if (!value) return null;
  const d = dayjsShow(value);
  return d.isValid() ? d : null;
}

export function resolveDoorsOpenMoment(
  start?: string | null,
  doorsOpen?: string | null
): Dayjs | null {
  const startMoment = parseShowDateTime(start ?? undefined);
  if (!startMoment) return null;

  const doorsMoment = parseShowDateTime(doorsOpen ?? undefined);
  if (doorsMoment && doorsMoment.isBefore(startMoment)) {
    return doorsMoment;
  }

  return startMoment.subtract(SHOW_DOORS_OPEN_MINUTES_BEFORE_START, "minute");
}

export function formatDoorsLine(start?: string | null, doorsOpen?: string | null): string {
  const doors = resolveDoorsOpenMoment(start, doorsOpen);
  const formatted = safeFormat(doors, "h:mm A");
  if (!formatted) return "";
  return `Doors open @ ${formatted}${SHOW_TIMEZONE_LABEL}`;
}

export function formatShowDateLine(start?: string | null): string {
  const d = parseShowDateTime(start ?? undefined);
  return safeFormat(d, "dddd, MMMM D");
}

export function formatShowStartTimeLine(start?: string | null): string {
  const d = parseShowDateTime(start ?? undefined);
  const formatted = safeFormat(d, "h:mm A");
  return formatted ? `${formatted}${SHOW_TIMEZONE_LABEL}` : "";
}

/** Variant label for ticket dropdowns (Eastern wall clock). */
export function formatShowVariantOptionLabel(variant: {
  variant_name?: string | null;
  start?: string | null;
}): string {
  const name = variant.variant_name?.trim();
  if (name) return name;
  const d = parseShowDateTime(variant.start ?? undefined);
  if (!d) return "Show";
  const datePart = safeFormat(d, "dddd, MMM D");
  const timePart = safeFormat(d, "h:mm A");
  if (!datePart || !timePart) return "Show";
  return `${datePart} · ${timePart}${SHOW_TIMEZONE_LABEL}`;
}
